import { Component, OnInit, OnChanges, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import type { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';

import { UserProfileService } from '../../../api/user-profile.service';
import { DataObject } from '../../../interfaces/data-objects';
import { LocaleService } from '../../../locale.service';
import { maskitoIndianCurrencyOptions } from '../../../utils/maskito-masks';

@Component({
  selector: 'iv-currency-field',
  templateUrl: './currency-field.component.html',
  styleUrls: ['./currency-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyFieldComponent),
      multi: true
    }
  ]
})
export class CurrencyFieldComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() controlName: string;
  @Input() label: string;
  @Input() optLabel: string;
  @Input() currency: string;
  @Input() prefixed = false;
  @Input() disabled = false;
  @Input() allowNegativeValue = false;
  @Input() padded = true;
  @Input() required = false;
  // Applicable to all of the Integer fields, Backend has max_digits=17
  @Input() decimalLimit = 2;
  @Input() integerLimit = 15;
  @Input() requireDecimal = false;
  // tslint:disable-next-line:no-input-rename
  @Input('fieldValue') _value: any;
  @Input() isToolTipRequired = false;
  @Input() tooltipText = '';
  @Input() tooltipConfig = {
    position: 'right'
  };
  @Input() customStyles = null;
  @Input() showAsterisk = false;
  @Input() userCountry: string;
  @Input() allowDecimal = true;
  @Input() thousandsSeparatorSymbol = ',';
  @Input() includeThousandsSeparator = true;
  @Input() formFieldClass: string = '';

  currencyOptions: DataObject;
  maskitoCurrencyOptions: MaskitoOptions;
  userLocale = 'en-US';

  prefixedNgClassObject = {}
  noPrefixedNgClassObject = {}

  get fieldValue() {
    return this._value;
  }

  set fieldValue(val) {
    if ((val || val === 0) && val !== '.') {
      this._value = val;
      if (val.replace) {
        const numericValue = this.getNumericValue(val);
        this.propagateChange(numericValue);
      } else {
        this.propagateChange(val);
      }
    } else if (val === '.') {
      this._value = '0.';
    } else {
      this._value = '0.00';
      this.propagateChange(this._value);
    }
  }

  propagateChange = (_: any) => { };
  propagateTouched = (_: any) => { };

  constructor(
    private userProfileService: UserProfileService,
    private localeService: LocaleService) { }

  ngOnInit() {
    this.init();
  }

  init() {
    this.userCountry = this.userProfileService.getCountryLocalStorage();
    this.userLocale = this.localeService.getLocale();
    this.maskitoCurrencyOptions = this.getCurrencyOptions();

    this.prefixedNgClassObject = {
      'overwrite-suffix-prefix-styles': this.label,
      'overwrite-no-label-min-height': !this.label,
      [this.formFieldClass]: this.formFieldClass !== null || this.formFieldClass !== '',
    };
    
    this.noPrefixedNgClassObject = {
      'overwrite-suffix-prefix-styles' : this.label,
      [this.formFieldClass]: this.formFieldClass !== null || this.formFieldClass !== '',
    };
  }

  ngOnChanges() {
    if (this.fieldValue) {
      this.propagateChange(this.fieldValue);
    }
  }

  writeValue(value) {
    this.fieldValue = value;
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn) {
    this.propagateTouched = fn;
  }

  getCurrencyOptions(): MaskitoOptions {
    let numberOptions = {};
    this.decimalLimit = +this.userProfileService.getCurrencyDecimalsLocalStorage();

    switch (this.userCountry) {
      default:
        numberOptions = {
          decimalZeroPadding: true,
          decimalSeparator: (this.allowDecimal || this.decimalLimit > 0) ? '.' : '',
          thousandSeparator: this.includeThousandsSeparator ? this.thousandsSeparatorSymbol : '',
          minusSign: this.allowNegativeValue ? '-' : '',
          precision: (this.allowDecimal || this.decimalLimit) ? this.decimalLimit : 0,
        };
        break;
    }

    this.currencyOptions = numberOptions;

    if (this.userCountry === 'IND') {
      return maskitoIndianCurrencyOptions(numberOptions);
    }

    return maskitoNumberOptionsGenerator(numberOptions);
  }

  // Code below added for - https://jira.cbre.com/browse/FIN-3635

  currencyPipeFn = (confirmedValue, config) => {
    if (config.rawValue === '.' && confirmedValue.indexOf('_') === confirmedValue.length - 1) {
      return '.';
    }
    return confirmedValue;
  }

  onBlur(val) {
    if (val === '0.') {
      this.writeValue('0.00');
    }
  }

  getNumericValue(val: string): string {
    return this.localeService.getNumericValue(val, this.userCountry);
  }

}
